import { useId, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import type { ApiClientKind } from '@/api/client'
import { BASELINE_NOTE_MAX_LENGTH, type TrackingBaseline, type TrackingBaselineInput } from '@/api/dataQuality'
import { formatBaselineDate } from '@/components/dataQuality'
import { firstPartyKeys } from '@/features/analytics/traffic/firstPartyQuery'
import { advertisingOverviewKeys } from '@/features/marketing/advertising/overviewQuery'
import { attributionKeys } from '@/features/marketing/attribution/attributionQuery'
import { CHANNEL_CHART_QUERY, overviewQueryKey } from '@/features/overview/hooks'
import { Card, EmptyState, LoadingBlock } from '@/design'
import { usePermissions } from '@/lib/access'
import { ApiError } from '@/lib/api'
import { toDateKey } from '@/lib/filters'

import './tracking.css'

/** The permission `PUT /data-quality/baseline` requires (Owner, Administrator). */
const EDIT_PERMISSION = 'settings.manage'

export const trackingBaselineKey = (kind: ApiClientKind) => ['tracking-baseline', kind] as const

/**
 * The cached reads whose `data_quality` block depends on the baseline, by the key roots
 * their own modules export (so a renamed key is a compile error here, not a stale cache):
 * first-party traffic (the Traffic page), attribution (its conversions and channel
 * table), the Ads overview and the Overview's channel chart.
 */
export const DATA_QUALITY_QUERY_ROOTS: ReadonlyArray<readonly unknown[]> = [
  firstPartyKeys.all,
  attributionKeys.all,
  advertisingOverviewKeys.all,
  overviewQueryKey(CHANNEL_CHART_QUERY),
]

/** Whether a cached read's key falls under one of those roots. */
export function readsDataQuality(queryKey: readonly unknown[]): boolean {
  return DATA_QUALITY_QUERY_ROOTS.some((root) => root.every((part, index) => queryKey[index] === part))
}

const SET_AT = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' })

/**
 * Why a date is refused before it is sent: none, or one after today. The API judges
 * "today" in the tenant's reporting time zone and refuses a later day with a 422, whose
 * message the form shows; this only saves a round trip in the common case.
 */
export function baselineDateError(date: string, today: string): string | null {
  if (date === '') return 'Choose the first day tracking is reliable from, or clear the baseline.'
  if (date > today) return 'The baseline cannot be after today: tracking cannot be reliable from a day that has not happened yet.'
  return null
}

/**
 * Settings › Tracking (wave 20; docs/architecture/data-quality.md): the tenant's tracking
 * baseline — the first day its browser tracking is reliable from. Every reader of reports
 * may see it (`analytics.read`), since it is why a page says its dates reach before it and
 * why insights do not compare across it; only an Owner or Administrator
 * (`settings.manage`) may change or clear it.
 */
export function TrackingSettingsPage() {
  const client = useApiClient()
  const { can } = usePermissions()
  const baseline = useQuery({ queryKey: trackingBaselineKey(client.kind), queryFn: () => client.trackingBaseline() })

  if (baseline.isPending) return <LoadingBlock rows={3} label="Loading the tracking baseline" />
  if (baseline.isError) return <LoadError error={baseline.error} />

  return (
    <div className="ci-tracking">
      <Card
        title="Tracking baseline"
        subtitle="The first day your website's tracking is reliable from. Reports say when their dates reach before it, and insights never compare across it."
      >
        <BaselineSummary baseline={baseline.data} canEdit={can(EDIT_PERMISSION)} />
      </Card>
      {can(EDIT_PERMISSION) ? <BaselineEditor baseline={baseline.data} /> : null}
    </div>
  )
}

/**
 * The baseline as it stands. Who set it is the sign-in subject the API records (as the
 * audit log shows it): named to those who may change it, and "an administrator" to
 * everyone else, who has no use for an identity provider's id.
 */
function BaselineSummary({ baseline, canEdit }: { baseline: TrackingBaseline; canEdit: boolean }) {
  const date = formatBaselineDate(baseline.trackingReliableFrom)
  const setAt = baseline.setAt ? SET_AT.format(new Date(baseline.setAt)) : null
  return (
    <div className="ci-tracking__summary" data-testid="tracking-baseline">
      {date ? (
        <p className="ci-tracking__date">
          Tracking reliable from <strong>{date}</strong>
        </p>
      ) : (
        <p className="ci-tracking__date">No tracking baseline is set. Every report treats all its dates as fully tracked.</p>
      )}
      {baseline.note || (baseline.setBy && setAt) ? (
        <dl className="ci-tracking__meta">
          {baseline.note ? (
            <div>
              <dt>Note</dt>
              <dd>{baseline.note}</dd>
            </div>
          ) : null}
          {baseline.setBy && setAt ? (
            <div>
              <dt>{date ? 'Set by' : 'Cleared by'}</dt>
              <dd>
                {canEdit ? baseline.setBy : 'An administrator'} · {setAt}
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}
    </div>
  )
}

/** Only for a viewer with `settings.manage`: a day no later than today, and an optional one-line note. */
function BaselineEditor({ baseline }: { baseline: TrackingBaseline }) {
  const client = useApiClient()
  const queryClient = useQueryClient()
  const [date, setDate] = useState(baseline.trackingReliableFrom ?? '')
  const [note, setNote] = useState(baseline.note ?? '')
  const [invalid, setInvalid] = useState<string | null>(null)
  // The baseline the form was filled from. A newer one — saved here, or by another
  // administrator and read on a refetch — refills it, so a save never sends a stale value.
  const [filledFrom, setFilledFrom] = useState(baseline.setAt)
  if (baseline.setAt !== filledFrom) {
    setFilledFrom(baseline.setAt)
    setDate(baseline.trackingReliableFrom ?? '')
    setNote(baseline.note ?? '')
    setInvalid(null)
  }
  const today = toDateKey(new Date())
  const errorId = useId()

  const save = useMutation({
    mutationFn: (input: TrackingBaselineInput) => client.setTrackingBaseline(input),
    onSuccess: (next) => {
      queryClient.setQueryData(trackingBaselineKey(client.kind), next)
      // The reports whose `data_quality` block reads the baseline: each is asked for again when next shown.
      void queryClient.invalidateQueries({ predicate: (query) => readsDataQuality(query.queryKey), refetchType: 'none' })
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    const error = baselineDateError(date, today)
    setInvalid(error)
    if (error === null) save.mutate({ trackingReliableFrom: date, note })
  }

  function clear() {
    setInvalid(null)
    // Audited and reversible, but every report and insight reads it at once. A clear
    // carries no note: a draft typed for a date change is never saved with it.
    if (window.confirm('Clear the tracking baseline? Reports and insights will treat every date as fully tracked.')) {
      save.mutate({ trackingReliableFrom: null, note: null })
    }
  }

  const message = invalid ?? apiMessage(save.error)
  return (
    <Card title="Change the baseline" subtitle="Owners and Administrators only. Every change is recorded in the audit log.">
      <form className="ci-tracking__form" aria-label="Change the tracking baseline" onSubmit={submit} noValidate>
        <label className="ci-field">
          <span className="ci-field__label">Reliable from</span>
          <input
            type="date"
            name="tracking_reliable_from"
            max={today}
            value={date}
            disabled={save.isPending}
            aria-invalid={invalid !== null || undefined}
            aria-describedby={message ? errorId : undefined}
            onChange={(event) => setDate(event.target.value)}
          />
        </label>
        <label className="ci-field ci-tracking__note">
          <span className="ci-field__label">Note (optional)</span>
          <input
            type="text"
            name="note"
            maxLength={BASELINE_NOTE_MAX_LENGTH}
            autoComplete="off"
            value={note}
            disabled={save.isPending}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
        <span className="ci-tracking__actions">
          <button type="submit" className="ci-button" data-variant="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save baseline'}
          </button>
          {baseline.trackingReliableFrom ? (
            <button type="button" className="ci-button" data-variant="ghost" disabled={save.isPending} onClick={clear}>
              Clear baseline
            </button>
          ) : null}
        </span>
      </form>
      {message ? (
        <p role="alert" id={errorId} className="ci-tracking__error">
          {message}
        </p>
      ) : null}
      {save.isSuccess && !message ? (
        <p role="status" className="ci-tracking__ok">
          Saved. Reports and the next insights run read the new baseline.
        </p>
      ) : null}
    </Card>
  )
}

/** Whatever the API said went wrong, in its own words (it never repeats what was sent). */
function apiMessage(error: unknown): string | null {
  if (!error) return null
  return error instanceof ApiError ? error.message : 'Something went wrong. Try again shortly.'
}

function LoadError({ error }: { error: unknown }) {
  const refused = error instanceof ApiError && error.status === 403
  return (
    <EmptyState
      variant={refused ? 'permission' : 'outage'}
      description={refused ? 'You need access to reports to see the tracking baseline.' : 'Could not load the tracking baseline. Try again shortly.'}
    />
  )
}
